import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { questionsApi } from '../lib/questions';
import { api } from '../lib/api';
import {
  ArrowLeft,
  ArrowRight,
  RotateCcw,
  Home,
  Shuffle,
  Check,
  ThumbsUp,
  ThumbsDown,
  Trophy,
  Dna,
  Beaker,
  Atom,
  Heart,
  RefreshCw,
  Sparkles,
  Lock
} from 'lucide-react';

const SUPABASE_URL = 'https://ffccmyllnijevswabqhr.supabase.co';

// Helper pro URL obrázku
const getImageUrl = (imageName) => {
  if (!imageName) return null;
  // PNG: začíná na 'i' nebo '7' nebo '9' a obsahuje velká písmena (7B, 7CH, 9CH)
  const isPng = imageName.startsWith('i') ||
                imageName.startsWith('7B') ||
                imageName.startsWith('7CH') ||
                imageName.startsWith('9CH');
  const ext = isPng ? 'png' : 'jpg';
  return `${SUPABASE_URL}/storage/v1/object/public/question-images/${imageName}.${ext}`;
};

const Flashcards = () => {
  const { category } = useParams();
  const navigate = useNavigate();
  const { user, subscription, loading: authLoading } = useAuth();

  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [loading, setLoading] = useState(true);
  const [knew, setKnew] = useState(new Set());
  const [didntKnow, setDidntKnow] = useState(new Set());
  const [isFinished, setIsFinished] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPaywall, setShowPaywall] = useState(false);

  const hasActiveSubscription = subscription?.hasSubscription;

  const categoryConfig = {
    Biologie: { icon: Dna, color: 'from-green-500 to-emerald-600', bgColor: 'bg-green-500', textColor: 'text-green-400', glowColor: 'shadow-green-500/20' },
    Chemie: { icon: Beaker, color: 'from-blue-500 to-indigo-600', bgColor: 'bg-blue-500', textColor: 'text-blue-400', glowColor: 'shadow-blue-500/20' },
    Fyzika: { icon: Atom, color: 'from-pink-500 to-rose-600', bgColor: 'bg-pink-500', textColor: 'text-pink-400', glowColor: 'shadow-pink-500/20' },
    Somatologie: { icon: Heart, color: 'from-teal-500 to-cyan-600', bgColor: 'bg-teal-500', textColor: 'text-teal-400', glowColor: 'shadow-teal-500/20' }
  };

  const config = categoryConfig[category] || categoryConfig.Biologie;
  const CategoryIcon = config.icon;

  // Přesměrovat nepřihlášeného uživatele
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { state: { redirectTo: `/flashcards/${category}` } });
    }
  }, [authLoading, user, navigate, category]);

  // Pro přihlášeného bez předplatného zobrazit paywall
  useEffect(() => {
    if (!authLoading && user && !hasActiveSubscription) {
      setShowPaywall(true);
    }
  }, [authLoading, user, hasActiveSubscription]);

  useEffect(() => {
    if (user && hasActiveSubscription) {
      loadQuestions();
    }
  }, [category, user, hasActiveSubscription]);

  const loadQuestions = async () => {
    try {
      setLoading(true);
      const data = await questionsApi.getQuestionsSorted(category);
      setQuestions(data);
    } catch (error) {
      console.error('Error loading questions:', error);
    } finally {
      setLoading(false);
    }
  };

  const currentQuestion = questions[currentIndex];

  const handleFlip = () => {
    if (isAnimating) return;
    setIsAnimating(true);
    setIsFlipped(!isFlipped);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const handleKnew = () => {
    const questionId = currentQuestion.id;
    setKnew(prev => new Set([...prev, questionId]));
    setDidntKnow(prev => {
      const newSet = new Set(prev);
      newSet.delete(questionId);
      return newSet;
    });
    goToNext();
  };

  const handleDidntKnow = () => {
    const questionId = currentQuestion.id;
    setDidntKnow(prev => new Set([...prev, questionId]));
    setKnew(prev => {
      const newSet = new Set(prev);
      newSet.delete(questionId);
      return newSet;
    });
    goToNext();
  };

  const goToNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setIsFlipped(false);
    } else {
      setIsFinished(true);
    }
  };

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(prev => prev + 1);
      setIsFlipped(false);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
      setIsFlipped(false);
    }
  };

  const handleShuffle = () => {
    const shuffled = [...questions].sort(() => Math.random() - 0.5);
    setQuestions(shuffled);
    setCurrentIndex(0);
    setIsFlipped(false);
  };

  const handleReset = () => {
    setKnew(new Set());
    setDidntKnow(new Set());
    setCurrentIndex(0);
    setIsFlipped(false);
    setIsFinished(false);
  };

  const handleRetryWrong = () => {
    const wrongQuestions = questions.filter(q => didntKnow.has(q.id));
    if (wrongQuestions.length > 0) {
      setQuestions(wrongQuestions);
      setKnew(new Set());
      setDidntKnow(new Set());
      setCurrentIndex(0);
      setIsFlipped(false);
      setIsFinished(false);
    }
  };

  // Zobrazit paywall pro uživatele bez předplatného
  if (showPaywall) {
    const plans = [
      { key: '1_month', name: '1 měsíc', price: 249, pricePerMonth: 249 },
      { key: '3_months', name: '3 měsíce', price: 699, pricePerMonth: 233 },
      { key: '6_months', name: '6 měsíců', price: 1290, pricePerMonth: 215, popular: true },
    ];

    const handleSubscribe = async (planKey) => {
      try {
        const response = await api.createCheckoutSession(planKey, user.id, user.email);
        if (response.url) {
          window.location.href = response.url;
        } else if (response.error) {
          alert('Chyba: ' + response.error);
        }
      } catch (error) {
        console.error('Error:', error);
        alert('Nepodařilo se vytvořit platbu. Zkuste to znovu.');
      }
    };

    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-12">
        <div className="max-w-2xl mx-auto px-4">
          <div className="glass rounded-2xl p-8 text-center">
            <div className={`w-20 h-20 mx-auto rounded-full bg-gradient-to-br ${config.color} flex items-center justify-center mb-6`}>
              <Lock size={40} className="text-white" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">Flashcards - {category}</h1>
            <p className="text-dark-400 mb-6">
              Flashcards vyžadují aktivní předplatné
            </p>

            <div className="grid grid-cols-3 gap-3 mb-6">
              {plans.map((plan) => (
                <div
                  key={plan.key}
                  onClick={() => handleSubscribe(plan.key)}
                  className={`relative rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
                    plan.popular
                      ? 'bg-gradient-to-b from-primary-900/50 to-dark-800 border-2 border-primary-500'
                      : 'glass border border-dark-700 hover:border-dark-600'
                  }`}
                >
                  {plan.popular && (
                    <div className="absolute -top-2.5 left-1/2 transform -translate-x-1/2">
                      <div className="flex items-center gap-1 px-2 py-0.5 rounded-full gradient-bg text-white text-xs font-semibold">
                        <Sparkles size={10} />
                        <span>Nejlepší</span>
                      </div>
                    </div>
                  )}
                  <div className="text-center">
                    <h4 className="text-white font-semibold text-sm">{plan.name}</h4>
                    <div className="flex items-baseline justify-center gap-1 mt-1">
                      <span className="text-xl font-bold text-white">{plan.price}</span>
                      <span className="text-dark-400 text-xs">Kč</span>
                    </div>
                    <p className="text-dark-500 text-xs mt-0.5">
                      {plan.pricePerMonth} Kč/měsíc
                    </p>
                  </div>
                  <div className={`mt-3 py-1.5 rounded-lg text-sm font-semibold ${
                    plan.popular
                      ? 'gradient-bg text-white'
                      : 'bg-dark-700 text-white'
                  }`}>
                    Vybrat
                  </div>
                </div>
              ))}
            </div>

            <button
              onClick={() => navigate('/dashboard')}
              className="text-dark-400 hover:text-white transition-colors"
            >
              Zpět na Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-dark-950 pt-24 flex items-center justify-center">
        <div className="text-center">
          <div className="relative">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary-500 to-purple-600 animate-pulse mx-auto mb-4 flex items-center justify-center">
              <Sparkles className="text-white animate-spin" size={28} />
            </div>
          </div>
          <div className="text-white font-medium">Načítám kartičky...</div>
          <div className="text-dark-400 text-sm mt-1">{category}</div>
        </div>
      </div>
    );
  }

  // Výsledková obrazovka
  if (isFinished) {
    const knewCount = knew.size;
    const didntKnowCount = didntKnow.size;
    const totalAnswered = knewCount + didntKnowCount;
    const percentage = totalAnswered > 0 ? Math.round((knewCount / totalAnswered) * 100) : 0;

    return (
      <div className="min-h-screen bg-dark-950 pt-24 pb-12">
        {/* Background Effects */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -right-40 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-1/4 -left-40 w-96 h-96 bg-purple-500/10 rounded-full blur-3xl animate-pulse"></div>
        </div>

        <div className="relative max-w-2xl mx-auto px-4">
          <div className="glass rounded-3xl p-8 text-center border border-white/5 shadow-2xl">
            <div className="w-24 h-24 mx-auto rounded-3xl bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-6 shadow-lg shadow-amber-500/30 animate-bounce">
              <Trophy size={48} className="text-white" />
            </div>

            <h1 className="text-3xl font-bold text-white mb-2">Flashcards dokončeny!</h1>
            <p className="text-dark-400 mb-8 flex items-center justify-center gap-2">
              <CategoryIcon size={18} className={config.textColor} />
              {category}
            </p>

            <div className="grid grid-cols-3 gap-4 mb-8">
              <div className="glass rounded-2xl p-5 border border-green-500/20 bg-green-500/5">
                <p className="text-4xl font-bold text-green-400">{knewCount}</p>
                <p className="text-dark-400 text-sm mt-1">Věděl(a)</p>
              </div>
              <div className="glass rounded-2xl p-5 border border-red-500/20 bg-red-500/5">
                <p className="text-4xl font-bold text-red-400">{didntKnowCount}</p>
                <p className="text-dark-400 text-sm mt-1">Nevěděl(a)</p>
              </div>
              <div className="glass rounded-2xl p-5 border border-primary-500/20 bg-primary-500/5">
                <p className="text-4xl font-bold gradient-text">{percentage}%</p>
                <p className="text-dark-400 text-sm mt-1">Úspěšnost</p>
              </div>
            </div>

            <div className="mb-8">
              <div className="h-3 bg-dark-800 rounded-full overflow-hidden flex shadow-inner">
                <div
                  className="bg-gradient-to-r from-green-500 to-emerald-400 transition-all duration-1000 ease-out"
                  style={{ width: `${(knewCount / questions.length) * 100}%` }}
                />
                <div
                  className="bg-gradient-to-r from-red-500 to-rose-400 transition-all duration-1000 ease-out"
                  style={{ width: `${(didntKnowCount / questions.length) * 100}%` }}
                />
              </div>
            </div>

            <div className={`mb-8 p-5 rounded-2xl ${percentage >= 70 ? 'bg-green-500/10 border border-green-500/30' : percentage >= 40 ? 'bg-amber-500/10 border border-amber-500/30' : 'bg-red-500/10 border border-red-500/30'}`}>
              <p className={`font-medium text-lg ${percentage >= 70 ? 'text-green-400' : percentage >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                {percentage >= 90 && 'Výborně! Máš to skvěle naučené! 🎉'}
                {percentage >= 70 && percentage < 90 && 'Velmi dobře! Ještě trochu procvičit a budeš mít vše! 💪'}
                {percentage >= 40 && percentage < 70 && 'Dobrý základ! Zkus procvičit ty, které nevíš. 📚'}
                {percentage < 40 && 'Nevadí! Procvičuj dál, zlepšíš se! 🚀'}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              {didntKnowCount > 0 && (
                <button
                  onClick={handleRetryWrong}
                  className="px-6 py-4 rounded-2xl bg-red-500/20 text-red-400 font-semibold hover:bg-red-500/30 transition-all flex items-center justify-center space-x-2 border border-red-500/30 hover:scale-105"
                >
                  <RotateCcw size={20} />
                  <span>Procvičit neznámé ({didntKnowCount})</span>
                </button>
              )}
              <button
                onClick={handleReset}
                className="px-6 py-4 rounded-2xl bg-gradient-to-r from-primary-500 to-purple-600 text-white font-semibold hover:opacity-90 transition-all flex items-center justify-center space-x-2 shadow-lg shadow-primary-500/30 hover:scale-105"
              >
                <RotateCcw size={20} />
                <span>Začít znovu</span>
              </button>
              <button
                onClick={() => navigate('/flashcards')}
                className="px-6 py-4 rounded-2xl bg-dark-700 text-white font-semibold hover:bg-dark-600 transition-all flex items-center justify-center space-x-2 hover:scale-105"
              >
                <Home size={20} />
                <span>Kategorie</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-dark-950 pt-24 pb-12">
      {/* Background Effects */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-40 -right-40 w-80 h-80 bg-primary-500/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-40 -left-40 w-80 h-80 bg-purple-500/10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-2xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate('/flashcards')}
            className="flex items-center space-x-2 text-dark-400 hover:text-white transition-colors group"
          >
            <ArrowLeft size={20} className="group-hover:-translate-x-1 transition-transform" />
            <span>Zpět</span>
          </button>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleShuffle}
              className="p-2.5 rounded-xl bg-dark-800/80 text-dark-400 hover:text-white hover:bg-dark-700 transition-all hover:scale-110"
              title="Zamíchat"
            >
              <Shuffle size={18} />
            </button>
            <button
              onClick={handleReset}
              className="p-2.5 rounded-xl bg-dark-800/80 text-dark-400 hover:text-white hover:bg-dark-700 transition-all hover:scale-110"
              title="Reset"
            >
              <RotateCcw size={18} />
            </button>
            <button
              onClick={() => setIsFinished(true)}
              className="px-4 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-orange-500 text-white text-sm font-medium hover:opacity-90 transition-all shadow-lg shadow-amber-500/20 hover:scale-105"
            >
              Dokončit
            </button>
          </div>
        </div>

        {/* Progress slider */}
        <div className="mb-8">
          <input
            type="range"
            min="0"
            max={questions.length - 1}
            value={currentIndex}
            onChange={(e) => {
              const newIndex = parseInt(e.target.value);
              setCurrentIndex(newIndex);
              setIsFlipped(false);
            }}
            className="w-full h-2 bg-dark-800 rounded-full appearance-none cursor-pointer
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-5
              [&::-webkit-slider-thumb]:h-5
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-gradient-to-r
              [&::-webkit-slider-thumb]:from-primary-500
              [&::-webkit-slider-thumb]:to-purple-500
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:shadow-lg
              [&::-webkit-slider-thumb]:shadow-primary-500/50
              [&::-webkit-slider-thumb]:transition-transform
              [&::-webkit-slider-thumb]:hover:scale-125
              [&::-moz-range-thumb]:w-5
              [&::-moz-range-thumb]:h-5
              [&::-moz-range-thumb]:rounded-full
              [&::-moz-range-thumb]:bg-primary-500
              [&::-moz-range-thumb]:cursor-pointer
              [&::-moz-range-thumb]:border-0"
            style={{
              background: `linear-gradient(to right, rgb(139, 92, 246) 0%, rgb(168, 85, 247) ${((currentIndex + 1) / questions.length) * 100}%, rgb(31, 41, 55) ${((currentIndex + 1) / questions.length) * 100}%, rgb(31, 41, 55) 100%)`
            }}
          />
          <div className="flex justify-between text-dark-500 text-xs mt-2">
            <span>1</span>
            <span className="text-dark-400 font-medium">{currentIndex + 1} / {questions.length}</span>
            <span>{questions.length}</span>
          </div>
        </div>

        {/* Flashcard - Single card with conditional content */}
        <div
          className={`glass rounded-3xl p-5 mb-6 cursor-pointer border border-white/10 shadow-2xl ${config.glowColor} transition-all duration-300 hover:shadow-xl`}
          onClick={handleFlip}
        >
          {/* Card Header */}
          <div className="flex items-center justify-between mb-3">
            <div className={`flex items-center space-x-2 px-3 py-1.5 rounded-full bg-gradient-to-r ${config.color} shadow-lg`}>
              <CategoryIcon size={14} className="text-white" />
              <span className="text-white text-xs font-semibold uppercase tracking-wide">{category}</span>
            </div>
            <div className="flex items-center space-x-3 text-sm">
              {isFlipped && (
                <div className="flex items-center space-x-1.5 px-2.5 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                  <Check size={12} className="text-green-400" />
                  <span className="text-green-400 text-xs font-semibold">Odpověď</span>
                </div>
              )}
              <span className="flex items-center space-x-1 px-2 py-1 rounded-lg bg-green-500/10 text-green-400">
                <ThumbsUp size={14} />
                <span className="font-medium">{knew.size}</span>
              </span>
              <span className="flex items-center space-x-1 px-2 py-1 rounded-lg bg-red-500/10 text-red-400">
                <ThumbsDown size={14} />
                <span className="font-medium">{didntKnow.size}</span>
              </span>
            </div>
          </div>

          {/* Question */}
          <h2 className="text-base font-semibold text-white mb-4 leading-relaxed whitespace-pre-line">
            {currentQuestion?.question_text}
          </h2>

          {/* Question Image */}
          {currentQuestion?.image_name && (
            <div className="mb-4">
              <img
                src={getImageUrl(currentQuestion.image_name)}
                alt="Obrázek k otázce"
                className="max-w-full h-auto rounded-xl border border-dark-700 mx-auto"
                style={{ maxHeight: '200px' }}
              />
            </div>
          )}

          {/* Options */}
          <div className="space-y-2">
            {currentQuestion?.options?.map((option, idx) => {
              const isCorrect = idx === currentQuestion.correct_answer_index;
              const showCorrect = isFlipped && isCorrect;
              const showWrong = isFlipped && !isCorrect;

              return (
                <div
                  key={idx}
                  className={`p-3 rounded-xl text-sm flex items-center transition-all duration-300 ${
                    showCorrect
                      ? 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 border-2 border-green-500 text-white'
                      : showWrong
                      ? 'bg-dark-800/30 border border-dark-700/30 text-dark-500'
                      : 'bg-dark-800/60 border border-dark-700/50 text-dark-300'
                  }`}
                >
                  <span className={`inline-flex items-center justify-center w-6 h-6 rounded-lg mr-3 font-medium text-xs flex-shrink-0 ${
                    showCorrect ? 'bg-green-500 text-white' : 'bg-dark-700 text-dark-400'
                  }`}>
                    {String.fromCharCode(65 + idx)}
                  </span>
                  <span className="flex-1">{option}</span>
                  {showCorrect && <Check size={18} className="text-green-400 ml-2 flex-shrink-0" />}
                </div>
              );
            })}
          </div>

          {/* Explanation - only when flipped */}
          {isFlipped && currentQuestion?.explanation && (
            <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-primary-500/10 to-purple-500/10 border border-primary-500/20 animate-fadeIn">
              <div className="flex items-center space-x-2 mb-1">
                <Sparkles size={12} className="text-primary-400" />
                <p className="text-primary-400 text-xs font-semibold uppercase tracking-wide">Vysvětlení</p>
              </div>
              <p className="text-dark-300 text-sm leading-relaxed">
                {currentQuestion.explanation}
              </p>
            </div>
          )}

          {/* Flip indicator */}
          <div className="flex items-center justify-center mt-3 pt-3 border-t border-dark-800/50">
            <div className={`flex items-center space-x-2 transition-colors ${isFlipped ? 'text-primary-400' : 'text-dark-500'}`}>
              <RefreshCw size={16} className={isFlipped ? '' : 'animate-pulse'} />
              <span className="text-sm">{isFlipped ? 'Klikni pro otočení zpět' : 'Klikni pro zobrazení odpovědi'}</span>
            </div>
          </div>
        </div>

        {/* Věděl/Nevěděl tlačítka */}
        <div className={`flex justify-center gap-4 mb-6 transition-all duration-500 ${isFlipped ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-4 pointer-events-none'}`}>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDidntKnow();
            }}
            className="flex-1 max-w-[180px] py-4 px-6 rounded-2xl bg-gradient-to-r from-red-500/20 to-rose-500/20 border border-red-500/40 text-red-400 font-semibold hover:from-red-500/30 hover:to-rose-500/30 transition-all flex items-center justify-center space-x-2 hover:scale-105 shadow-lg shadow-red-500/10"
          >
            <ThumbsDown size={22} />
            <span>Nevěděl(a)</span>
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleKnew();
            }}
            className="flex-1 max-w-[180px] py-4 px-6 rounded-2xl bg-gradient-to-r from-green-500/20 to-emerald-500/20 border border-green-500/40 text-green-400 font-semibold hover:from-green-500/30 hover:to-emerald-500/30 transition-all flex items-center justify-center space-x-2 hover:scale-105 shadow-lg shadow-green-500/10"
          >
            <ThumbsUp size={22} />
            <span>Věděl(a)</span>
          </button>
        </div>

        {/* Navigation */}
        <div className="flex items-center justify-center gap-6 mb-8">
          <button
            onClick={handlePrevious}
            disabled={currentIndex === 0}
            className={`p-4 rounded-2xl transition-all ${
              currentIndex === 0
                ? 'bg-dark-800/50 text-dark-600 cursor-not-allowed'
                : 'bg-dark-700 text-white hover:bg-dark-600 hover:scale-110 shadow-lg'
            }`}
          >
            <ArrowLeft size={24} />
          </button>

          <div className="flex items-center space-x-1">
            {[...Array(Math.min(5, questions.length))].map((_, i) => {
              const dotIndex = Math.max(0, Math.min(currentIndex - 2, questions.length - 5)) + i;
              return (
                <div
                  key={i}
                  className={`rounded-full transition-all duration-300 ${
                    dotIndex === currentIndex
                      ? 'w-8 h-2 bg-gradient-to-r from-primary-500 to-purple-500'
                      : 'w-2 h-2 bg-dark-700'
                  }`}
                />
              );
            })}
          </div>

          <button
            onClick={handleNext}
            disabled={currentIndex === questions.length - 1}
            className={`p-4 rounded-2xl transition-all ${
              currentIndex === questions.length - 1
                ? 'bg-dark-800/50 text-dark-600 cursor-not-allowed'
                : 'bg-gradient-to-r from-primary-500 to-purple-600 text-white hover:opacity-90 hover:scale-110 shadow-lg shadow-primary-500/30'
            }`}
          >
            <ArrowRight size={24} />
          </button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-4 gap-3">
          <div className="glass rounded-2xl p-4 text-center border border-white/5 hover:border-white/10 transition-colors">
            <p className="text-2xl font-bold text-white">{questions.length}</p>
            <p className="text-dark-500 text-xs mt-1">Celkem</p>
          </div>
          <div className="glass rounded-2xl p-4 text-center border border-green-500/20 bg-green-500/5 hover:bg-green-500/10 transition-colors">
            <p className="text-2xl font-bold text-green-400">{knew.size}</p>
            <p className="text-dark-500 text-xs mt-1">Věděl(a)</p>
          </div>
          <div className="glass rounded-2xl p-4 text-center border border-red-500/20 bg-red-500/5 hover:bg-red-500/10 transition-colors">
            <p className="text-2xl font-bold text-red-400">{didntKnow.size}</p>
            <p className="text-dark-500 text-xs mt-1">Nevěděl(a)</p>
          </div>
          <div className="glass rounded-2xl p-4 text-center border border-amber-500/20 bg-amber-500/5 hover:bg-amber-500/10 transition-colors">
            <p className="text-2xl font-bold text-amber-400">{questions.length - knew.size - didntKnow.size}</p>
            <p className="text-dark-500 text-xs mt-1">Zbývá</p>
          </div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Flashcards;
